import React from "react"
import { useTranslation } from "react-i18next"
import {
  withFormik,
  FormikProps,
  FormikErrors,
  Form,
  Field,
  FieldProps,
  FormikHelpers as FormikActions,
} from "formik"
import { isValid, isToday, addBusinessDays } from "date-fns"
import { Grid, styled } from "@mui/material"

import i18n from "config/i18n"
import Input from "components/ui/Input"
import InputDatePicker from "components/ui/InputDatePicker"
import InputSelect from "components/ui/InputSelect"
import Button from "components/ui/Submit"
import { PHONE_REGEX } from "helpers/client"
import { IssueFormValues, SelectOptionType, IssueTimeslotOptions } from "types"

interface FormProps {
  initialValues: IssueFormValues
  timeslotOptions: IssueTimeslotOptions
  phoneCodeOptions: SelectOptionType[]
  onSubmit: (values: IssueFormValues, actions: FormikActions<object>) => void
}

type Props = FormikProps<IssueFormValues> & FormProps

const IssueForm = ({
  timeslotOptions,
  phoneCodeOptions,
  errors,
  setFieldValue,
  isSubmitting,
}: Props) => {
  const { t } = useTranslation()

  const isDisabled = (date: Date): boolean => {
    const nextBusinessDay = addBusinessDays(new Date(), 1)

    if (isToday(date)) {
      return false
    } else return date.toDateString() !== nextBusinessDay.toDateString()
  }

  return (
    <Form>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Field name="date">
            {({ field }: FieldProps) => (
              <InputDatePicker
                {...field}
                disabled={isSubmitting}
                shouldDisableDate={(date) => isDisabled(date)}
                helperText={errors.date}
                onChange={(value: Date | null) => {
                  if (value) {
                    setFieldValue("date", value)
                  }
                }}
              />
            )}
          </Field>
        </Grid>
        <Grid item xs={6}>
          <Field name="timeslot">
            {({ field }: FieldProps) => (
              <InputSelect fullWidth disabled={isSubmitting} {...field}>
                {timeslotOptions.map((c: SelectOptionType) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </InputSelect>
            )}
          </Field>
        </Grid>

        <Grid item xs={4}>
          <Field name="phone_code">
            {({ field }: FieldProps) => (
              <InputSelect fullWidth disabled={isSubmitting} {...field}>
                {phoneCodeOptions.map((c: SelectOptionType) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </InputSelect>
            )}
          </Field>
        </Grid>
        <Grid item xs={8}>
          <Field name="phone_number">
            {({ field }: FieldProps) => (
              <Input
                fullWidth
                label={t("features.issue.phone")}
                helperText={errors.phone_number}
                disabled={isSubmitting}
                {...field}
              />
            )}
          </Field>
        </Grid>
      </Grid>
      <Submit type="submit" disabled={isSubmitting}>
        {t("features.issue.form.submit")}
      </Submit>
    </Form>
  )
}

const IssueFormWithFormik = withFormik<FormProps, IssueFormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues }) => initialValues,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  handleSubmit: (values, actions) => actions.props.onSubmit(values, actions),
  validate: (values: IssueFormValues) => {
    const errors: FormikErrors<IssueFormValues> = {}
    const { phone_number, date, timeslot } = values

    if (!phone_number) {
      errors.phone_number = i18n.t("validation.field.required", {
        field: i18n.t("features.issue.phone"),
      })
    } else if (!PHONE_REGEX.test(phone_number)) {
      errors.phone_number = i18n.t("validation.field.invalid", {
        field: i18n.t("features.issue.phone"),
      })
    }

    if (!date) {
      errors.date = i18n.t("validation.field.required", {
        field: i18n.t("features.issue.date"),
      })
    } else if (!isValid(date)) {
      errors.date = i18n.t("validation.field.invalid", {
        field: i18n.t("features.issue.date"),
      })
    }

    if (!timeslot) {
      errors.timeslot = i18n.t("validation.field.required", {
        field: i18n.t("features.issue.timeslot"),
      })
    }

    return errors
  },
})(IssueForm)

export default IssueFormWithFormik

const Submit = styled(Button)(() => ({
  borderRadius: "21px",
  display: "block",
  margin: "24px auto 0 auto",
  width: "206px",
}))
